import { useContext, useEffect } from "react";

import { MktgDataContext } from "@swa-ui/application";
import { useLocation } from "@swa-ui/browser";
import { swaDate } from "@swa-ui/date";

const FORTY_EIGHT_HOURS_IN_MINUTES = 1152;

export function useUpcomingTripsAnalytics(
  upcomingTrips,
  upcomingtrips_button,
  searchToken,
  upgradedBoardingEligibility
) {
  const { pathname } = useLocation();
  const { setMktgPageData } = useContext(MktgDataContext);

  useEffect(() => {
    if (pathname === "/" || pathname.includes("/landing/home-page/")) {
      setMktgPageData(getNextTripDetails(), "upcomingTrips");
    }
  }, [pathname, searchToken, upcomingTrips]);

  const getBoardingPositions = (segments) =>
    segments
      ?.map(
        ({ boarding_details }) =>
          `${boarding_details?.boarding_pass_group}${boarding_details?.boarding_pass_position}`
      )
      .join(",");

  function getNextTripDetails() {
    const {
      bounds,
      upcoming_travel_at,
      permissions,
      pnr_type: pnrType,
      pnr_sub_type: pnrSubType,
    } = upcomingTrips?.[0] ?? [];
    const boundsNotFlown = bounds?.filter((bound) => !bound?.flown) ?? [];
    const segments = boundsNotFlown[0]?.segments ?? [];
    const arrivalTime = segments[0]?.arrival_at;
    const upcomingTripsAnalyticsObject = {
      at_boarding_position: "",
      atnexttripplanechangeovnt: "",
      customer_daystotrip: "",
      domain_name: "",
      nexttrip_arrivaltime: "",
      nexttrip_departuredate: "",
      nexttrip_departuretime: "",
      nexttrip_destination: "",
      nexttrip_origin: "",
      nexttrip_segmentairports: "",
      search_token: "",
      upcomingtrips_button: "",
      upcomingtrips_count: "0",
      upgradeboarding_elr_available: "0",
    };

    if (upcomingTrips?.length > 0) {
      const { eligibleTimeRangeForUB = false, isUpgradedBoardingEligible = false } =
        upgradedBoardingEligibility;
      const minutesToDeparture = getMinutesToDeparture(upcoming_travel_at);
      const seatsAssigned = upcomingTrips[0]?.passengers?.some(
        (passenger) => passenger?.seats?.length > 0
      );

      if (minutesToDeparture) {
        upcomingTripsAnalyticsObject["customer_minutestodeparture"] = minutesToDeparture;
      }

      if (seatsAssigned) {
        upcomingTripsAnalyticsObject["seatsassigned"] = "1";
      }

      upcomingTripsAnalyticsObject["banner_upcomingtrips"] = "1";
      upcomingTripsAnalyticsObject["customer_daystotrip"] = `${Math.floor(
        swaDate(upcoming_travel_at).diff(swaDate(), "days")
      )}`;
      upcomingTripsAnalyticsObject["domain_name"] = window.location.host;
      upcomingTripsAnalyticsObject["nexttrip_departuredate"] = `${swaDate(
        upcoming_travel_at
      )?.format("YYYY-MM-DD")}`;

      upcomingTripsAnalyticsObject["nexttrip_departuretime"] = `${swaDate(
        upcoming_travel_at
      )?.format("HH:mm")}`;
      upcomingTripsAnalyticsObject["nexttrip_arrivaltime"] = `${swaDate(arrivalTime)?.format(
        "HH:mm"
      )}`;
      permissions?.can_upgrade && (upcomingTripsAnalyticsObject["upgradeboard_available"] = "1");
      upcomingTripsAnalyticsObject["nexttrip_origin"] = boundsNotFlown[0]?.origination_airport_code;
      upcomingTripsAnalyticsObject["nexttrip_destination"] =
        boundsNotFlown[0]?.destination_airport_code;
      upcomingTripsAnalyticsObject["nexttrip_segmentairports"] = getSegmentAirports(segments);
      boundsNotFlown[0]?.overnight &&
        (upcomingTripsAnalyticsObject["atnexttripplanechangeovnt"] = "1");
      segments?.forEach((segment) => {
        segment.boarding_details?.boarding_pass_group &&
          (upcomingTripsAnalyticsObject["at_boarding_position"] = getBoardingPositions(segments));
      });
      upcomingTripsAnalyticsObject["page_description"] = "banner:upcoming trips";
      upcomingTripsAnalyticsObject["pnr_origin"] = `${pnrType}|${pnrSubType}`;
      upcomingTripsAnalyticsObject["upcomingtrips_button"] = upcomingtrips_button;
      upcomingTripsAnalyticsObject["upcomingtrips_count"] = `${upcomingTrips.length}`;
      upcomingTripsAnalyticsObject["upgradeboarding_elr_available"] =
        isUpgradedBoardingEligible && eligibleTimeRangeForUB ? "1" : "0";
      upcomingTripsAnalyticsObject["search_token"] = searchToken ? searchToken : "";
    }

    return upcomingTripsAnalyticsObject;
  }

  function getSegmentAirports(segments) {
    return segments
      .map((segment) => `${segment.origination_airport_code}:${segment.destination_airport_code}`)
      .join(",");
  }

  function getMinutesToDeparture(upcomingTravelAt) {
    const diff = swaDate(upcomingTravelAt).diff(swaDate(), "minutes");

    return diff > FORTY_EIGHT_HOURS_IN_MINUTES ? 0 : diff;
  }
}
